import { Chart } from 'primereact/chart';
import { FC, useMemo, useRef, useState } from 'react';
import { DiagnosticStatus, filterStudentsByStatus, percentageLabelsPlugin } from './helper/chartHelper';
import StudentDiagnosticModal from './StudentDiagnosticModal';
import StudentsCloserLookCard from './StudentsCloserLookCard';
import { ChartEvent, ActiveElement, TooltipItem } from 'chart.js';
import { Check, Route } from 'lucide-react';
import { DiagnosticsStatusByClassReportQuery$data } from '../../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';

interface Props {
  diagnosticResults: DiagnosticsStatusByClassReportQuery$data['classDiagnosticStatusReport'];
}

const chartPlugins = [percentageLabelsPlugin];

const TeacherDiagnosticDashboardChartWrapper: FC<Props> = ({ diagnosticResults }) => {
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<DiagnosticStatus>(DiagnosticStatus.COMPLETED);
  const chartRef = useRef<Chart>(null);

  const completedCount = filterStudentsByStatus(diagnosticResults, DiagnosticStatus.COMPLETED).length;
  const inProgressCount = filterStudentsByStatus(diagnosticResults, DiagnosticStatus.IN_PROGRESS).length;
  const notStartedCount = filterStudentsByStatus(diagnosticResults, DiagnosticStatus.NOT_STARTED).length;

  const isChartCompleted = completedCount === diagnosticResults.length;

  const chartData = useMemo(
    () => ({
      labels: ['Completed', 'In Progress', 'Not Started'],
      datasets: [
        {
          data: [completedCount, inProgressCount, notStartedCount],
          backgroundColor: ['#42C5A6', '#5776FC', '#D3D3D3'],
          borderColor: '#FFFFFF',
          borderWidth: isChartCompleted ? 0 : 4,
          hoverBorderColor: ['#42C5A6', '#5776FC', '#D3D3D3'],
          hoverBorderWidth: isChartCompleted ? 0 : 4,
          hoverColor: ['#42C5A6', '#1259FB', '#D3D3D3'],
        },
      ],
    }),
    [completedCount, inProgressCount, notStartedCount, isChartCompleted],
  );

  const chartOptions = useMemo(
    () => ({
      options: {
        interaction: {
          mode: 'point',
        },
      },
      // Empty band around the pie so the `outsideSlice` tooltip has room to land
      // outside the slices instead of overlapping the on-slice percentage labels.
      // The canvas is ~280px (see the chart container width), leaving a ~200px pie.
      layout: {
        padding: 40,
      },
      plugins: {
        title: {
          display: false,
          text: 'Diagnostic Chart',
        },
        percentageLabels: {
          textColor: isChartCompleted ? '#FFFFFF' : undefined,
        },
        legend: {
          display: false,
        },
        tooltip: {
          position: 'outsideSlice',
          callbacks: {
            label: (context: TooltipItem<'pie'>) => {
              const count = context.parsed;
              return `${count} ${count === 1 ? 'student' : 'students'}`;
            },
            title: () => {
              return '';
            },
          },
          backgroundColor: '#393939',
          displayColors: false,
          bodyFont: {
            size: 16,
            family: 'Atkinson Hyperlegible, sans-serif',
            weight: 'bold',
          },
          caretPadding: 6,
        },
      },

      onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
        if (!elements.length) return;
        const index = elements[0].index;
        const statusMap: DiagnosticStatus[] = [
          DiagnosticStatus.COMPLETED,
          DiagnosticStatus.IN_PROGRESS,
          DiagnosticStatus.NOT_STARTED,
        ];

        setSelectedStatus(statusMap[index]);
        setShowStudentsModal(true);
      },
    }),
    [isChartCompleted],
  );

  return (
    <>
      <div className="tw-flex lg:tw-flex-row tw-flex-col tw-gap-y-2 lg:tw-gap-x-2">
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-4">
          {!isChartCompleted && (
            <h3 className="tw-text-xl tw-font-bold tw-font-atkinson">
              {completedCount} of {diagnosticResults.length} students completed
            </h3>
          )}
          <Chart
            type="pie"
            ref={chartRef}
            className="tw-w-[280px]"
            data={chartData}
            options={chartOptions}
            plugins={chartPlugins}
          />
          <div className="tw-flex tw-flex-row tw-gap-x-8">
            {!isChartCompleted ? (
              <>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="7" fill={`${!completedCount ? '#6DFED4' : '#00D492'}`} />
                  </svg>
                  <p
                    className={`tw-font-atkinson tw-m-0 tw-text-base ${
                      !completedCount ? 'tw-text-neutral-700' : 'tw-text-neutral-950 tw-font-bold'
                    }`}
                  >
                    Completed
                  </p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="7" fill={`${!inProgressCount ? '#9FABFD' : '#5776FC'}`} />
                  </svg>
                  <p
                    className={`tw-font-atkinson tw-m-0 tw-text-base ${
                      !inProgressCount ? 'tw-text-neutral-700' : 'tw-text-neutral-950 tw-font-bold'
                    }`}
                  >
                    In-progress
                  </p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="7" fill={`${!notStartedCount ? '#E1E1E1' : '#D3D3D3'}`} />
                  </svg>
                  <p
                    className={`tw-font-atkinson tw-m-0 tw-text-base ${
                      !notStartedCount ? 'tw-text-neutral-700' : 'tw-text-neutral-950 tw-font-bold'
                    }`}
                  >
                    Not started
                  </p>
                </div>
              </>
            ) : (
              <div></div>
            )}
          </div>
        </div>
        {!isChartCompleted ? (
          <StudentsCloserLookCard diagnosticResults={diagnosticResults} />
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-y-2 tw-items-start tw-justify-center">
            <h1 className="tw-text-xl tw-font-sora tw-font-bold">And just like that — done!</h1>
            <p className="tw-text-base tw-font-atkinson tw-mt-0">
              All students in this class completed the diagnostic.
            </p>
            <div className="tw-flex tw-flex-row tw-gap-x-2">
              <span className="tw-rounded-3xl tw-border-solid tw-border tw-border-green-300 tw-bg-green-50 tw-flex tw-flex-row tw-items-center tw-gap-x-2 tw-px-4 tw-py-2">
                <Check size={20} className="tw-text-green-800" />
                <span className="tw-text-base tw-text-green-800 tw-font-atkinson tw-font-bold">Baselines captured</span>
              </span>
              <span className="tw-rounded-3xl tw-border-solid tw-border tw-border-amber-300 tw-bg-amber-50 tw-flex tw-flex-row tw-items-center tw-gap-x-2 tw-px-4 tw-py-2">
                <Route size={20} className="tw-text-amber-900" />
                <span className="tw-text-base tw-text-amber-900 tw-font-atkinson tw-font-bold">
                  Roadmaps personalized
                </span>
              </span>
            </div>
          </div>
        )}
      </div>
      <StudentDiagnosticModal
        visible={showStudentsModal}
        onHide={() => setShowStudentsModal(false)}
        selectedStatus={selectedStatus}
        students={diagnosticResults}
      />
    </>
  );
};

export default TeacherDiagnosticDashboardChartWrapper;
